import { NgModule } from "@angular/core";
import { RouterModule, Routes} from "@angular/router";
import { RoutesSlugs } from "@app/routes-slugs";
import { NotFirstTimeUserGuard } from "@app/guards/not-first-time.guard";
import { loggedInUserGuard } from "@app/presentation/route-guards/logged-in-user.guard";
import { loggedOutUserGuard } from "@app/presentation/route-guards/logged-out.guard";
import { playerAlreadySelectedGuard } from "./presentation/route-guards/player-already-selected.guard";
import { isNativeGuard } from "@app/guards/is-native.guard";
import { isWalletAvailableGuard } from "@app/presentation/route-guards/is-wallet-available.guard";
import { adminOnlyGuard } from "@app/presentation/route-guards/admin-only.guard";


const routes: Routes = [
  {
    path: RoutesSlugs.REGISTER,
    canMatch: [NotFirstTimeUserGuard, loggedOutUserGuard],
    loadComponent: () =>
      import("./presentation/pages/register/register.page").then(
        (m) => m.RegisterPage
      ),
  },
  {
    path: RoutesSlugs.VERIFY_EMAIL,
    canMatch: [NotFirstTimeUserGuard, loggedOutUserGuard],
    loadComponent: () =>
      import("./presentation/pages/verify-email/verify-email.component").then(
        (m) => m.VerifyEmailComponent
      ),
  },
  {
    path: RoutesSlugs.LOGIN,
    canMatch: [NotFirstTimeUserGuard, loggedOutUserGuard],
    loadComponent: () =>
      import("./presentation/pages/login/login.page").then((m) => m.LoginPage),
  },
  {
    path: RoutesSlugs.NO_INTERNET,
    loadComponent: () =>
      import("./presentation/pages/no-internet/no-internet.page").then(
        (m) => m.NoInternetPage
      ),
  },
  {
    path: RoutesSlugs.RESET_PASSWORD,
    canMatch: [NotFirstTimeUserGuard, loggedOutUserGuard],
    loadComponent: () =>
      import("./presentation/pages/reset-password/reset-password.page").then(
        (m) => m.ResetPasswordPage
      ),
  },
  {
    path: RoutesSlugs.COMPLETE_PROFILE,
    loadComponent: () =>
      import(
        "./presentation/pages/complete-profile/complete-profile.page"
      ).then((m) => m.CompleteProfilePage),
  },
  {
    path: RoutesSlugs.ONBOARDING,
    loadComponent: () =>
      import("./presentation/pages/onboarding/onboarding.page").then(
        (m) => m.OnboardingPage
      ),
  },
  //Routes that have the navigation bar
  {
    path: "",
    loadComponent: () =>
      import("./presentation/pages/nav-bar/nav-bar.page").then(
        (m) => m.NavBarPage
      ),
    canActivateChild: [loggedInUserGuard],
    children: [
      {
        path: RoutesSlugs.LOGOUT,
        runGuardsAndResolvers: "always",
        loadComponent: () =>
          import("./presentation/pages/config/logout.page").then(
            (m) => m.LogoutPage
          ),
      },
      {
        path: RoutesSlugs.HOME,
        runGuardsAndResolvers: "always",
        loadComponent: () =>
          import("./presentation/pages/home/home.component").then(
            (m) => m.HomeComponent
          ),
      },
      {
        path: RoutesSlugs.STATISTICS,
        runGuardsAndResolvers: "always",
        loadComponent: () =>
          import("./presentation/pages/statistics/statistics.page").then(
            (m) => m.StatisticsPage
          ),
      },
      {
        path: RoutesSlugs.MATCH,
        runGuardsAndResolvers: "always",
        loadComponent: () =>
          import("./presentation/pages/matches/matches.page").then(
            (m) => m.MatchesPage
          ),
      },
      {
        path: `${RoutesSlugs.MATCH}/:matchId/${RoutesSlugs.MATCH_COMPARE}`,
        runGuardsAndResolvers: "always",
        loadComponent: () =>
          import("./presentation/pages/match-compare/match-compare.page").then(
            (m) => m.MatchComparePage
          ),
      },
      {
        path: RoutesSlugs.EXAMPLE_MATCH,
        runGuardsAndResolvers: "always",
        loadComponent: () =>
          import(
            "./presentation/pages/match-detail/match-detail-example.page"
          ).then((m) => m.MatchDetailExamplePage),
      },
      {
        path: RoutesSlugs.EXAMPLE_MATCH_COMPARE,
        runGuardsAndResolvers: "always",
        loadComponent: () =>
          import(
            "./presentation/pages/match-compare/match-compare-example.page"
          ).then((m) => m.MatchCompareExamplePage),
      },
      {
        path: `${RoutesSlugs.MATCH}/:matchId/${RoutesSlugs.PLAYER_IDENTIFICATION}`,
        runGuardsAndResolvers: "always",
        canActivate: [playerAlreadySelectedGuard],
        loadComponent: () =>
          import("./presentation/pages/before-match/before-match.page").then(
            (m) => m.BeforeMatchPage
          ),
      },
      {
        path: `${RoutesSlugs.MATCH}/:matchId`,
        runGuardsAndResolvers: "always",
        loadComponent: () =>
          import("./presentation/pages/match-detail/match-detail.page").then(
            (m) => m.MatchDetailPage
          ),
      },
      {
        path: `${RoutesSlugs.SHARED_STATISTICS}/:matchId`,
        runGuardsAndResolvers: "always",
        loadComponent: () =>
          import(
            "./presentation/pages/match-detail/shared-statistics.page"
          ).then((m) => m.SharedStatisticsPage),
      },
      {
        path: `${RoutesSlugs.MATCH}/:matchId/${RoutesSlugs.PAYMENT_MISSING}`,
        runGuardsAndResolvers: "always",
        loadComponent: () =>
          import(
            "./presentation/pages/match-payment-missing/match-payment-missing.page"
          ).then((m) => m.MatchPaymentMissingPage),
      },
      {
        path: `${RoutesSlugs.MATCH}/:matchId/${RoutesSlugs.MATCH_DETAIL}`,
        runGuardsAndResolvers: "always",
        loadComponent: () =>
          import(
            "./presentation/pages/shared-booking-detail/shared-booking-detail.page"
          ).then((m) => m.SharedBookingDetailPage),
        data: { mode: "match" },
      },
      {
        path: `${RoutesSlugs.FAKE_SECTIONS}`,
        runGuardsAndResolvers: "always",
        loadComponent: () =>
          import("./presentation/pages/fake-sections/fake-sections.page").then(
            (m) => m.FakeSectionsPage
          ),
      },
      {
        path: RoutesSlugs.BOOKINGS,
        runGuardsAndResolvers: "always",
        loadComponent: () =>
          import(
            "./presentation/pages/bookings-list/list-bookings.component"
          ).then((m) => m.ListBookingsComponent),
      },
      {
        path: `${RoutesSlugs.BOOKINGS}/add`,
        runGuardsAndResolvers: "always",
        loadComponent: () =>
          import(
            "./presentation/pages/booking-add-edit/add-edit-booking.component"
          ).then((m) => m.AddEditBookingComponent),
      },
      {
        path: `${RoutesSlugs.BOOKINGS}/fully-integrated`,
        runGuardsAndResolvers: "always",
        canActivate: [],
        loadComponent: () =>
          import(
            "./presentation/pages/fully-integrated-club/fully-integrated-club.component"
          ).then((m) => m.FullyIntegratedClubComponent),
      },
      {
        path: `${RoutesSlugs.BOOKINGS}/:id/edit`,
        runGuardsAndResolvers: "always",
        loadComponent: () =>
          import(
            "./presentation/pages/booking-add-edit/add-edit-booking.component"
          ).then((m) => m.AddEditBookingComponent),
      },
      {
        path: `${RoutesSlugs.BOOKINGS}/:id/detail`,
        runGuardsAndResolvers: "always",
        loadComponent: () =>
          import(
            "./presentation/pages/shared-booking-detail/shared-booking-detail.page"
          ).then((m) => m.SharedBookingDetailPage),
        data: { mode: "booking" },
      },
      {
        path: `${RoutesSlugs.CHECKOUT}/:id`,
        runGuardsAndResolvers: "always",
        loadComponent: () =>
          import(
            "@app/presentation/pages/booking-checkout/booking-checkout.component"
          ).then((m) => m.BookingCheckoutComponent),
      },
      {
        path: RoutesSlugs.NOTIFICATIONS,
        canActivate: [isNativeGuard],
        runGuardsAndResolvers: "always",
        loadComponent: () =>
          import(
            "@app/presentation/pages/notifications/notifications.page"
          ).then((m) => m.NotificationsComponent),
      },
      {
        path: RoutesSlugs.CONFIG,
        runGuardsAndResolvers: "always",
        loadComponent: () =>
          import("./presentation/pages/config/config.page").then(
            (m) => m.ConfigPage
          ),
        children: [
          {
            path: RoutesSlugs.EDIT_PROFILE,
            runGuardsAndResolvers: "always",
            loadComponent: () =>
              import(
                "./presentation/pages/edit-profile/edit-profile.component"
              ).then((m) => m.EditProfileComponent),
          },
          {
            path: RoutesSlugs.CONTACT,
            runGuardsAndResolvers: "always",
            loadComponent: () =>
              import("./presentation/pages/contact/contact.page").then(
                (m) => m.ContactPage
              ),
          },
          {
            path: RoutesSlugs.ADMIN_IMPERSONATE,
            canActivate: [adminOnlyGuard],
            runGuardsAndResolvers: "always",
            loadComponent: () =>
              import(
                "./presentation/pages/config/admin-impersonate/admin-impersonate.page"
              ).then((m) => m.AdminImpersonatePage),
          },
        ],
      },
      {
        path: RoutesSlugs.WALLET,
        canActivate: [isWalletAvailableGuard],
        runGuardsAndResolvers: "always",
        loadComponent: () =>
          import("./presentation/pages/wallet/wallet.page").then(
            (m) => m.WalletPage
          ),
      },
      {
        path: RoutesSlugs.TEST_COMPONENTS,
        canActivate: [isWalletAvailableGuard],
        runGuardsAndResolvers: "always",
        loadComponent: () =>
          import(
            "./presentation/pages/test-components/test-components.component"
          ).then((m) => m.TestComponentsComponent),
      },
    ],
  },

  // Catch-all route for unknown paths
  {
    path: "**",
    loadComponent: () =>
      import("./presentation/pages/not-found/not-found.component").then(
        (m) => m.NotFoundComponent
      ),
  },
];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule],
})
export class AppRoutingModule {}
